import {useMemo} from 'react';
import {canOpenRemotely, getCodeDependsOnLockType} from '@chekin/shared/remote-access';
import {useTranslation} from 'react-i18next';
import {useSummary} from 'context/summary';
import {
  useIVComputedDetails,
  useGuestGroup,
  useAvailableLocks,
  useFetchLocks,
  useFetchOffers,
  useRemoteAccessEnabled,
} from 'hooks';
import {useCanShareRegistration} from 'hooks/useCanShareRegistration';
import {useStaySummary} from 'hooks/useStaySummary';
import {formatShortTime} from 'utils/dates';
import {useChatSettings} from 'hooks/useChatSettings';
import {useScanSummary} from 'hooks/useScanSummary';
import {useGetGuestId} from 'hooks/useGetGuestId';
import {useChatAttachmentsEnabled} from 'pages/ChatView/hooks/useChatAttachmentsEnabled';
import {useFaqEnabled} from 'hooks/useFaqEnabled';
import {useFetchGuidebooks} from 'hooks/guidebooks/useFetchGuidebooks';
import {useReservation} from 'context/reservation';
import {useCaptureModeStore} from '@chekin/shared';
import {useContactsVerificationStore} from 'hooks/iv/useContactsVerificationStore';
import {
  useRegistrationStep,
  type RegistrationStepKey,
} from 'components/RegistrationProgress';
import {buildVelaContent} from '../engine/buildVelaContent';
import {getStayOffers} from '../engine/stayOffers';
import {VelaScene, type VelaContent, type VelaContext} from '../engine/types';
import {velaRules} from '../rules';
import {
  getVelaHeader,
  getVelaProgressCopy,
  type VelaHeaderCopy,
  type VelaProgressVariant,
} from '../copy/velaCopy';
import {useVelaScene} from '../scenes/useVelaScene';
import {useVelaDepositStore} from './useVelaDepositStore';
import {useVelaTaxesStore} from './useVelaTaxesStore';
import {useVelaUpsellStore} from './useVelaUpsellStore';
import {useVelaCartStore} from './useVelaCartStore';
import {useVelaGuidebookNavState} from './useVelaGuidebookNavStore';
import {useVelaGuestFormStore} from './useVelaGuestFormStore';
import {useKeysState} from 'components/RemoteAccess/useKeysState';
import {useRegistrationResultStore} from 'components/RegistrationResult/useRegistrationResultStore';
import {getConditionRows} from 'components/RemoteAccess/lockConditions';

const REGISTRATION_RESULT_SCENES = new Set<VelaScene>([
  VelaScene.GuestRegistering,
  VelaScene.GuestRegistered,
  VelaScene.GuestRegistrationFailed,
]);

export type VelaProgress = {
  stepKeys: RegistrationStepKey[];
  activeIndex: number;
  skippedIndices: number[];
  title: string;
  intro: string;
};

export type VelaResult = {
  scene: VelaScene;
  header: VelaHeaderCopy;
  progress: VelaProgress | null;
  content: VelaContent;
  isEmpty: boolean;
};

export function useVela(): VelaResult | null {
  const {t} = useTranslation('vela');
  const scene = useVelaScene();
  const {isIVEnabled, isIVCompleted, biomatch} = useIVComputedDetails();
  const {isGuestRegistrationComplete, countryCode, data: summary} = useSummary();
  const ivStep = useRegistrationStep();

  const {guestGroup, totalGuests, totalIncompletedGuests} = useGuestGroup();
  const canShareRegistration = useCanShareRegistration();
  const isRemoteAccessEnabled = useRemoteAccessEnabled();
  const {checkInTime, checkOutTime} = useStaySummary();
  const {isChatEnabled} = useChatSettings();
  const isChatAttachmentsEnabled = useChatAttachmentsEnabled();
  const isFaqEnabled = useFaqEnabled();
  const {reservationId = '', housingId} = useReservation();
  const hasStayOffers =
    scene === VelaScene.GuestRegistration || scene === VelaScene.GuestRegistered;
  const {data: offers} = useFetchOffers({
    housingId: hasStayOffers && summary?.upselling?.active ? housingId : undefined,
  });
  const stayOffers = useMemo(() => getStayOffers(offers?.results), [offers?.results]);
  const {data: guidebooks} = useFetchGuidebooks({reservationId});
  const hasGuidebooks = (guidebooks?.items?.length ?? 0) > 0;
  const captureMode = useCaptureModeStore(state => state.mode);
  const contactsChannel = useContactsVerificationStore(state => state.channel);
  const contactsStep = useContactsVerificationStore(state => state.step);
  const contactsHasError = useContactsVerificationStore(state => state.hasError);
  const guestId = useGetGuestId();
  const {hasScan} = useScanSummary();
  const hasOptionalFields = useVelaGuestFormStore(state => state.hasOptionalFields);
  const {state: keysState} = useKeysState();
  const {lockConditions} = useAvailableLocks();
  const blockingKeyCondition = useMemo(
    () =>
      keysState === 'gated'
        ? getConditionRows(lockConditions?.incomplete_conditions).blocking?.kind ?? null
        : null,
    [keysState, lockConditions?.incomplete_conditions],
  );
  const {locks} = useFetchLocks({enabled: keysState !== 'gated'});
  const hasCodeKeys = Boolean(locks?.some(unlock => getCodeDependsOnLockType(unlock)));
  const hasLinkKeys = Boolean(locks?.some(canOpenRemotely));
  const deposit = useVelaDepositStore(state => state.deposit);
  const taxes = useVelaTaxesStore(state => state.taxes);
  const isUpsellOnRequest = useVelaUpsellStore(state => state.isOnRequest);
  const isUpsellLuggage = useVelaUpsellStore(state => state.isLuggage);
  const hasUpsellStorage = useVelaUpsellStore(state => state.hasStorage);
  const cartHasHolds = useVelaCartStore(state => state.hasHolds);
  const cartHasAuthorisations = useVelaCartStore(state => state.hasAuthorisations);
  const cartHasPayLater = useVelaCartStore(state => state.hasPayLater);
  const cartHasReceipts = useVelaCartStore(state => state.hasReceipts);
  const registeredFirstName = useRegistrationResultStore(state => state.firstName);
  const adults = guestGroup?.adults ?? 0;
  const minors = guestGroup?.children ?? 0;

  const hasGuidebookNav = useVelaGuidebookNavState(
    state => state.chapters.length > 0 || state.siblings.length > 0,
  );

  return useMemo(() => {
    if (!scene) return null;

    const ctx: VelaContext = {
      scene,
      isIVEnabled,
      isIVCompleted,
      biomatch,
      isRegistrationComplete: isGuestRegistrationComplete,
      countryCode,
      isDataPrefilled: hasScan || Boolean(guestId),
      hasDocumentScan: hasScan,
      hasOptionalFields,
      isChatEnabled,
      isChatAttachmentsEnabled,
      isFaqEnabled,
      hasGuidebooks,
      guestCount: {adults, minors},
      totalGuests,
      pendingGuests: totalIncompletedGuests,
      registeredFirstName,
      canShareRegistration,
      stayTimes: {
        checkIn: formatShortTime(checkInTime),
        checkOut: formatShortTime(checkOutTime),
      },
      stayOffers,
      isRemoteAccessEnabled,
      captureMode,
      contacts: {
        channel: contactsChannel,
        step: contactsStep,
        hasError: contactsHasError,
      },
      keysState,
      blockingKeyCondition,
      hasCodeKeys,
      hasLinkKeys,
      deposit,
      taxes,
      upsell: {
        isOnRequest: isUpsellOnRequest,
        isLuggage: isUpsellLuggage,
        hasStorage: hasUpsellStorage,
      },
      cart: {
        hasHolds: cartHasHolds,
        hasAuthorisations: cartHasAuthorisations,
        hasPayLater: cartHasPayLater,
        hasReceipts: cartHasReceipts,
      },
    };

    const content = buildVelaContent(ctx, t, velaRules);
    const header = getVelaHeader(t, ctx);

    let progress: VelaProgress | null = null;

    if (
      ivStep &&
      scene !== VelaScene.IVUnavailable &&
      !REGISTRATION_RESULT_SCENES.has(scene)
    ) {
      const isFormStep = ivStep.stepKeys[ivStep.activeIndex] === 'form';
      const variant: VelaProgressVariant =
        ivStep.skippedIndices.length > 0
          ? 'skipped'
          : isFormStep && isIVCompleted
          ? 'lastStep'
          : 'inProgress';
      progress = {
        stepKeys: ivStep.stepKeys,
        activeIndex: ivStep.activeIndex,
        skippedIndices: ivStep.skippedIndices,
        ...getVelaProgressCopy(t, variant),
      };
    }

    return {
      scene,
      header,
      progress,
      content,
      isEmpty:
        scene === VelaScene.GuidebookDetail
          ? !hasGuidebookNav
          : scene !== VelaScene.GuestRegistering &&
            !progress &&
            content.sections.length === 0,
    };
  }, [
    scene,
    registeredFirstName,
    isIVEnabled,
    isIVCompleted,
    biomatch,
    isGuestRegistrationComplete,
    countryCode,
    hasScan,
    hasOptionalFields,
    guestId,
    ivStep,
    isChatEnabled,
    isChatAttachmentsEnabled,
    isFaqEnabled,
    hasGuidebooks,
    captureMode,
    contactsChannel,
    contactsStep,
    contactsHasError,
    adults,
    minors,
    totalGuests,
    totalIncompletedGuests,
    canShareRegistration,
    checkInTime,
    checkOutTime,
    stayOffers,
    isRemoteAccessEnabled,
    keysState,
    blockingKeyCondition,
    hasCodeKeys,
    hasLinkKeys,
    deposit,
    taxes,
    isUpsellOnRequest,
    isUpsellLuggage,
    hasUpsellStorage,
    cartHasHolds,
    cartHasAuthorisations,
    cartHasPayLater,
    cartHasReceipts,
    hasGuidebookNav,
    t,
  ]);
}
